// =====================================================================
// OrderDocuments.jsx — Commandes prêtes v3 B (16-10-2026) — page PUBLIQUE /order/<token>/documents
// =====================================================================
// Bouton brun du mail « ready for collection » : le client nous envoie l'étiquette du transporteur et
// les documents (CMR, packing list, facture…) ; nous imprimons et expédions. Bilingue (anglais puis
// français), plein écran comme OrderPhotos.jsx. Le jeton du lien EST l'accès.
// Chaque fichier part SEUL dès qu'il est choisi (≤ 15 Mo ; le serveur vérifie son format réel) ;
// « Send / Envoyer » valide ensuite le lot (transporteur, type et copies de chaque fichier, 10 au plus).
// Un fichier ZPL est toujours une étiquette transporteur (type figé : seule une Zebra l'imprime).
// =====================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { publicReadyRdvDocs, errMsg } from '../services/api.js';
import {
  TYPES_DOCUMENT, MAX_FICHIERS, MAX_COPIES, ACCEPT, devinerType, verifFichierLocal, verifEnvoi, corpsEnvoi, texteManque,
  typeEffectif, tailleTexte, libelleType, dateMax, jourFerme, MSG_JOUR_FERME,
} from './commande-prete/rdvDocs.js';
import { useMetaPublique, Entete, Invalide, Chargement, ErreurChargement, Pied, S } from './commande-prete/PagePublique.jsx';

let compteur = 0;

export default function OrderDocuments() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [etat, setEtat] = useState('loading');
  const [transporteur, setTransporteur] = useState('');
  const [dateEnlevement, setDateEnlevement] = useState('');
  const [instructions, setInstructions] = useState('');
  const [fichiers, setFichiers] = useState([]);
  const [envoi, setEnvoi] = useState(false);
  const [msg, setMsg] = useState(null);
  const file = useRef(Promise.resolve());   // téléversements l'un après l'autre
  const retires = useRef(new Set());        // fichiers retirés avant leur tour
  const lu = useRef(false);
  const invalide = useRef(false);           // lien mort : plus rien n'est envoyé
  const cleEnvoi = useRef(null);            // même clé jusqu'à une réponse : un renvoi n'imprime jamais deux fois
  const input = useRef(null);
  useMetaPublique(data ? `${data.orderRef} — documents` : null);

  // silencieux : relecture après un envoi réussi — son échec n'efface jamais le « reçu ».
  const charger = useCallback(({ silencieux = false } = {}) => publicReadyRdvDocs.documents(token)
    .then((d) => {
      // 1re lecture : les fichiers déjà téléversés mais pas encore envoyés (page rechargée) sont repris.
      if (!lu.current && Array.isArray(d.enAttente) && d.enAttente.length) {
        setFichiers(d.enAttente.map((x) => ({
          cle: `f${++compteur}`, nom: x.nom, taille: x.taille, etat: 'pret', id: x.id, format: x.format,
          type: x.format === 'ZPL' ? 'ETIQUETTE' : (x.type || devinerType(x.nom)), copies: '1', fichier: null,
        })));
      }
      lu.current = true; setData(d); setEtat('ok');
    })
    .catch((e) => {
      if (e?.response?.status === 404) setEtat('invalid');
      else if (!lu.current) setEtat('erreur');   // (pas « lien invalide » : réseau / serveur — on peut réessayer)
      else if (!silencieux) setMsg({ ok: false, texte: errMsg(e) });
    }), [token]);
  useEffect(() => { charger(); }, [charger]);

  if (etat === 'loading') return <Chargement />;
  if (etat === 'invalid') return <Invalide />;
  if (etat === 'erreur') return <ErreurChargement onReessayer={() => { setEtat('loading'); charger(); }} />;

  const majFichier = (cle, champs) => setFichiers((l) => l.map((x) => (x.cle === cle ? { ...x, ...champs } : x)));

  function choisir(liste) {
    setMsg(null);
    let n = fichiers.filter((x) => x.etat !== 'refuse').length;
    const nouveaux = [...liste].map((fi) => {
      const pb = verifFichierLocal(fi, n);
      if (!pb) n += 1;
      return { cle: `f${++compteur}`, nom: fi.name, taille: fi.size, etat: pb ? 'refuse' : 'attente', msg: pb, type: devinerType(fi.name), copies: '1', fichier: pb ? null : fi };
    });
    setFichiers((l) => [...l, ...nouveaux]);
    for (const x of nouveaux.filter((y) => y.etat === 'attente')) {
      file.current = file.current.then(async () => {
        if (retires.current.has(x.cle) || invalide.current) return;
        majFichier(x.cle, { etat: 'envoi' });
        try {
          const r = await publicReadyRdvDocs.televerser(token, x.fichier);
          if (retires.current.has(x.cle)) { publicReadyRdvDocs.retirer(token, r.id).catch(() => {}); return; }
          majFichier(x.cle, { etat: 'pret', id: r.id, nom: r.nom, format: r.format, fichier: null, ...(r.format === 'ZPL' ? { type: 'ETIQUETTE' } : {}) });
        } catch (e) {
          if (e?.response?.status === 404) { invalide.current = true; setEtat('invalid'); return; }
          majFichier(x.cle, { etat: 'refuse', msg: errMsg(e), fichier: null });
        }
      });
    }
    if (input.current) input.current.value = '';
  }

  async function retirer(x) {
    retires.current.add(x.cle);
    setFichiers((l) => l.filter((y) => y.cle !== x.cle));
    if (x.etat === 'pret' && x.id) publicReadyRdvDocs.retirer(token, x.id).catch(() => {});
  }

  async function envoyer() {
    const pb = verifEnvoi({ transporteur, dateEnlevement, aujourdhui: data.aujourdhui, feries: data.feries || [], fichiers });
    if (pb.length) { setMsg({ ok: false, texte: texteManque(pb) }); return; }
    setEnvoi(true);
    if (!cleEnvoi.current) cleEnvoi.current = (globalThis.crypto?.randomUUID?.() || `l${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`);
    try {
      const r = await publicReadyRdvDocs.envoyer(token, corpsEnvoi({ transporteur, dateEnlevement, instructions, fichiers, lot: cleEnvoi.current }));
      cleEnvoi.current = null;
      setMsg({
        ok: true,
        texte: `✓ ${r.recus} file${r.recus > 1 ? 's' : ''} received. We will print ${r.recus > 1 ? 'them' : 'it'} and ship with ${r.transporteur}.`,
        texteFr: `✓ ${r.recus} fichier${r.recus > 1 ? 's' : ''} reçu${r.recus > 1 ? 's' : ''}. Nous imprimons et expédions avec ${r.transporteur}.`,
      });
      setFichiers((l) => l.filter((x) => x.etat !== 'pret'));
      setInstructions('');
      await charger({ silencieux: true });
    } catch (e) {
      if (e?.response?.status === 404 && e?.response?.data?.error === 'INVALID_LINK') { setEtat('invalid'); return; }
      // Refus du serveur (réponse reçue) : nouvelle clé au prochain envoi ; sans réponse : même clé (réessai sûr).
      if (e?.response) cleEnvoi.current = null;
      setMsg({ ok: false, texte: errMsg(e) });
      if (e?.response?.status === 409) charger({ silencieux: true });   // la liste « déjà reçus » se remet à jour
    } finally { setEnvoi(false); }
  }

  const actifs = fichiers.filter((x) => x.etat !== 'refuse').length;
  const enCours = fichiers.some((x) => x.etat === 'envoi' || x.etat === 'attente');

  return (
    <div style={S.wrap}>
      <Entete titreEn="Send us documents to print" titreFr="Envoyer des documents à imprimer" data={data} />

      {data.enlevee ? (
        <div role="alert" style={S.alerte}>This order has already been collected. / Cette commande a déjà été enlevée.</div>
      ) : (
        <fieldset disabled={envoi} style={S.form}>
          <div style={S.ligne}>
            <div style={S.champ}>
              <label htmlFor="d-transp" style={S.label}>Carrier / Transporteur *</label>
              <input id="d-transp" type="text" value={transporteur} maxLength={100} placeholder="e.g. DHL Freight, Kuehne+Nagel…"
                onChange={(e) => { setTransporteur(e.target.value); setMsg(null); }} style={S.input} />
            </div>
            <div style={S.champ}>
              <label htmlFor="d-date" style={S.label}>Pick-up date by the carrier / Date d'enlèvement</label>
              <input id="d-date" type="date" value={dateEnlevement} min={data.aujourdhui} max={dateMax(data.aujourdhui)}
                onChange={(e) => { setDateEnlevement(e.target.value); setMsg(null); }} style={S.input} />
              {dateEnlevement && jourFerme(dateEnlevement, data.feries || []) && <div role="alert" style={{ ...S.petit, color: '#9b2c22' }}>{MSG_JOUR_FERME}</div>}
            </div>
          </div>

          <div>
            <label htmlFor="d-fich" style={S.label}>{`Files (PDF, ZPL, PNG, JPG — 15 MB each, ${MAX_FICHIERS} at most) / Fichiers`}</label>
            <input id="d-fich" ref={input} type="file" multiple accept={ACCEPT} disabled={actifs >= MAX_FICHIERS}
              onChange={(e) => choisir(e.target.files || [])} style={{ ...S.input, padding: 8 }} />
            {actifs >= MAX_FICHIERS && <p style={S.petit}>{`${MAX_FICHIERS} files at most per sending. / ${MAX_FICHIERS} fichiers au plus par envoi.`}</p>}
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead><tr>
                <th style={S.th}>File / Fichier</th><th style={S.th}>What is it? / Qu'est-ce ?</th><th style={S.th}>Copies</th><th style={S.th}><span style={{ position: 'absolute', left: -9999 }}>Remove</span></th>
              </tr></thead>
              <tbody>
                {!fichiers.length && <tr><td colSpan={4} style={{ ...S.td, color: '#5b6b73' }}>No file yet. / Aucun fichier.</td></tr>}
                {fichiers.map((x) => (
                  <tr key={x.cle}>
                    <td style={S.td}>
                      <span style={{ fontFamily: 'Consolas, monospace', fontSize: 13 }}>{x.nom}</span>
                      <br /><span style={S.petit}>{tailleTexte(x.taille)}{x.format ? ` · ${x.format}` : ''}</span>
                      {x.etat === 'attente' && <><br /><span style={S.petit}>⏳ waiting… / en attente…</span></>}
                      {x.etat === 'envoi' && <><br /><span style={S.petit}>⏳ uploading… / envoi…</span></>}
                      {x.etat === 'refuse' && <><br /><span style={{ ...S.petit, color: '#9b2c22' }}>✕ {x.msg}</span></>}
                    </td>
                    <td style={S.td}>
                      {x.etat !== 'refuse' && (
                        <select aria-label={`Type — ${x.nom}`} value={typeEffectif(x) || ''} disabled={x.format === 'ZPL'}
                          onChange={(e) => { majFichier(x.cle, { type: e.target.value }); setMsg(null); }} style={{ ...S.input, minWidth: 190 }}>
                          <option value="">— choose / choisir —</option>
                          {TYPES_DOCUMENT.map((t) => <option key={t.code} value={t.code}>{`${t.en} / ${t.fr}`}</option>)}
                        </select>
                      )}
                      {x.format === 'ZPL' && <div style={S.petit}>ZPL = carrier label (Zebra). / ZPL = étiquette (Zebra).</div>}
                    </td>
                    <td style={S.td}>
                      {x.etat !== 'refuse' && (
                        <input type="number" min={1} max={MAX_COPIES} step={1} inputMode="numeric" aria-label={`Copies — ${x.nom}`} value={x.copies}
                          onChange={(e) => { majFichier(x.cle, { copies: e.target.value }); setMsg(null); }} style={{ ...S.input, width: 80 }} />
                      )}
                    </td>
                    <td style={S.td}>
                      <button type="button" aria-label={`Remove ${x.nom} / Retirer`} disabled={x.etat === 'envoi'} onClick={() => retirer(x)} style={S.boutonDiscret}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div>
            <label htmlFor="d-note" style={S.label}>Instructions</label>
            <textarea id="d-note" value={instructions} maxLength={1000} rows={3} placeholder="e.g. one label per pallet / une étiquette par palette"
              onChange={(e) => setInstructions(e.target.value)} style={{ ...S.input, minHeight: 80 }} />
          </div>
          <div>
            <button type="button" id="d-ok" onClick={envoyer} disabled={enCours} style={{ ...S.bouton, ...(enCours ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}>
              {envoi ? '⏳ …' : enCours ? 'Uploading… / Envoi des fichiers…' : 'Send / Envoyer'}
            </button>
          </div>
        </fieldset>
      )}
      {msg && (
        <div role={msg.ok ? 'status' : 'alert'} style={msg.ok ? S.ok : S.erreur} id="d-msg">
          {msg.texte}{msg.texteFr && <><br /><span>{msg.texteFr}</span></>}
        </div>
      )}

      {data.recus.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h2 style={{ fontSize: 17, margin: '0 0 6px' }}>Already received / Déjà reçus</h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead><tr><th style={S.th}>File / Fichier</th><th style={S.th}>Type</th><th style={S.th}>Copies</th><th style={S.th}>Carrier / Transporteur</th><th style={S.th}>Status / État</th></tr></thead>
              <tbody>
                {data.recus.map((d, i) => (
                  <tr key={`${d.nom}-${i}`}>
                    <td style={{ ...S.td, fontFamily: 'Consolas, monospace', fontSize: 13 }}>{d.nom}</td>
                    <td style={S.td}>{`${libelleType(d.type, 'en')} / ${libelleType(d.type, 'fr')}`}</td>
                    <td style={S.td}>{d.copies}</td>
                    <td style={S.td}>{d.transporteur || '—'}</td>
                    <td style={S.td}>{d.imprime ? 'Printed / Imprimé' : 'Received / Reçu'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <Pied />
    </div>
  );
}
